import {
  Directive,
  Input,
  ElementRef,
  Renderer2,
  OnInit,
  OnDestroy,
  inject
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';

/**
 * Directiva que deshabilita elementos si el usuario no tiene acceso al recurso SEUS.
 * A diferencia de *hasAccess, el elemento permanece visible pero no interactivo.
 * Útil cuando se quiere que el usuario vea que existe la funcionalidad pero no puede usarla.
 *
 * @example
 * ```html
 * <!-- Deshabilitar botón si no tiene permiso -->
 * <p-button [disableIfNoAccess]="'btnEditar'" label="Editar" (click)="editar()" />
 *
 * <!-- Con tooltip personalizado -->
 * <p-button
 *   [disableIfNoAccess]="'btnEliminar'"
 *   noAccessTooltip="Contacte al administrador para obtener permisos"
 *   label="Eliminar" />
 *
 * <!-- En inputs -->
 * <input [disableIfNoAccess]="'campoSensible'" pInputText />
 *
 * <!-- En dropdowns -->
 * <p-dropdown [disableIfNoAccess]="'selectorAvanzado'" [options]="opciones" />
 * ```
 *
 * @remarks
 * - El elemento inicia deshabilitado mientras se verifica el permiso
 * - Si hay error de red, permanece deshabilitado (fail-safe)
 * - Aplica estilos visuales de deshabilitado (opacity, pointer-events)
 * - Compatible con componentes PrimeNG (agrega clase p-disabled)
 *
 * @see IdentityServiceImpl.isAuthorized
 */
@Directive({
  selector: '[disableIfNoAccess]',
  standalone: true
})
export class DisableIfNoAccessDirective implements OnInit, OnDestroy {

  private readonly identityService = inject(IdentityServiceImpl);
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  private subscription?: Subscription;

  /**
   * ID del recurso SEUS a verificar.
   * Debe coincidir con el ID configurado en el sistema SEUS.
   */
  @Input() disableIfNoAccess = '';

  /**
   * Tooltip a mostrar cuando el elemento está deshabilitado por falta de permisos.
   * Por defecto muestra un mensaje genérico.
   */
  @Input() noAccessTooltip = 'No tiene permisos para esta acción';

  ngOnInit(): void {
    if (!this.disableIfNoAccess) {
      // Si no se especifica recurso, no hacer nada
      return;
    }

    // Deshabilitar mientras se verifica (evitar clicks antes de validar)
    this.setDisabled(true);

    this.subscription = this.identityService.isAuthorized(this.disableIfNoAccess).subscribe({
      next: (hasAccess) => {
        this.setDisabled(!hasAccess);
        if (!hasAccess) {
          this.renderer.setAttribute(this.el.nativeElement, 'title', this.noAccessTooltip);
        } else {
          this.renderer.removeAttribute(this.el.nativeElement, 'title');
        }
      },
      error: (err) => {
        console.warn(`[DisableIfNoAccessDirective] Error verificando '${this.disableIfNoAccess}':`, err);
        // Fail-safe: mantener deshabilitado en caso de error
        this.setDisabled(true);
        this.renderer.setAttribute(this.el.nativeElement, 'title', 'Error verificando permisos');
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  /**
   * Aplica o quita el estado deshabilitado del elemento.
   * Maneja tanto elementos HTML nativos como componentes PrimeNG.
   */
  private setDisabled(disabled: boolean): void {
    const el = this.el.nativeElement;

    if (disabled) {
      // Atributo HTML estándar
      this.renderer.setAttribute(el, 'disabled', 'true');
      // Clase PrimeNG para estilos consistentes
      this.renderer.addClass(el, 'p-disabled');
      // Evitar interacción
      this.renderer.setStyle(el, 'pointer-events', 'none');
      this.renderer.setStyle(el, 'opacity', '0.6');
      // Cursor visual
      this.renderer.setStyle(el, 'cursor', 'not-allowed');
    } else {
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.removeClass(el, 'p-disabled');
      this.renderer.removeStyle(el, 'pointer-events');
      this.renderer.removeStyle(el, 'opacity');
      this.renderer.removeStyle(el, 'cursor');
    }
  }
}
